import { LoaderFileKeys } from '../app/constant/AppConstants';
import { TableScreen, VariationId } from '../app/screen/TableScreen';
import { ScreenManager, ScreenManagerEvents, ScreenTransitionDirection } from '../managers/ScreenManager';
import { InitExtensionMethods } from '../extensions/MyExtensions';
import { CardsManager } from '../app/managers/CardsManager';
import { AppLocalization, LanguageId } from '../app/localization/AppLocalization';
import { AppDebug } from '../app/debug/AppDebug';
import { LobbyLayout, LobbyScreen } from '../app/screen/LobbyScreen';
import { OptionsScreen } from '../app/screen/OptionsScreen';
import { DealCompleteScreen } from '../app/screen/DealCompleteScreen';
import { AppLocalStorage } from '../app/managers/AppLocalStorage';
import { AppObjectPool } from '../app/pooling/AppObjectPool';
import { LoadingScreen, LoadingScreenEvents } from '../app/screen/LoadingScreen';
import { StatisticsScreen } from '../app/screen/StatisticsScreen';
import { RulesScreen } from '../app/screen/RulesScreen';
import { StatisticsDataManager } from '../app/managers/StatisticsDataManager';
import { AppAudioPlayer, SoundEffectKeys } from '../audio/AppAudioPlayer';
import { LobbyVariationItem } from '../app/screen/lobby/LobbyVariationItem';
import { CardDecksManager } from '../app/managers/CardDecksManager';
import { TableStateMachine, TrumpSuit } from '../app/state/TableStateMachine';
import { logger } from '../debug/Logger';
import { ThemeDataManager } from '../app/managers/ThemeDataManager';
import { LayoutDataManager, OptionsItemKeys } from '../app/managers/LayoutDataManager';
import { OptionsItemLiveData, OptionsItemThemeItemData, TableData } from '../types/types';
import { RectSize } from '../utils/RectSize';
import { SmartBackground } from '../screens/SmartBackground';
import { AILevel } from '../app/ai/TableAI';
import { FocusManager } from '../managers/FocusManager';
import { GTMTracking, GTMTrackingContinueOrNewGame } from '../app/managers/GTMTracking';
import { GetMultiAtlasScalingVariantDataByScale } from '../utils/Utils';
import { AvatarsVariantsData, LobbyVariantsData, ThemesVariantsData, UIVariantsData } from '../app/data/AppConstants';
import { ChangedItemData, LiveDataChangeEvents, OptionsDataManager } from '../app/managers/OptionsDataManager';


export enum ScreenId {
	LOBBY,
	TABLE,
}

export default class GameScene extends Phaser.Scene {
	public static instance: GameScene;

	public bg: SmartBackground;
	public loadingScreen: LoadingScreen;
	public lobbyScreen: LobbyScreen;
	public optionsScreen: OptionsScreen;
	public dealCompleteScreen: DealCompleteScreen;
	public statisticsScreen: StatisticsScreen;
	public rulesScreen: RulesScreen;
	public tableScreen: TableScreen;
	private sfx_json: any;
	private bgData: any;
	private CATCH_EXCEPTIONS_RELOAD_GAME: boolean = true;

	public constructor() {
		super({
			key: 'game',
			/**
			 * labs: file pack: https://phaser.io/examples/v3/category/loader/file-pack
			 * docs: PackFilesSection: https://newdocs.phaser.io/docs/3.55.2/Phaser.Types.Loader.FileTypes.PackFileSection
			 * docs: FileConfig: https://newdocs.phaser.io/docs/3.55.2/Phaser.Types.Loader.FileConfig
			 * docs: FileTypes(!): https://photonstorm.github.io/phaser3-docs/Phaser.Types.Loader.FileTypes.html
			 */
			pack: {
				files: [
					// {
					//     type: 'json',
					//     key: LoaderFileKeys.masterConfig_json,
					//     // url: "./settings/theme.json"
					//     url: "./settings/masterConfig.json"
					// },
					// 'loading' atlas file
					// FileConfig (load image)
					// {
					//     key: "image1",
					//     type: "image",
					//     url: "./atlas/loading-0.png"
					// },
					// AtlasJSONFileConfig (load atlas texture + json)
					{
						type: 'atlas',
						key: LoaderFileKeys.loading,
						//@ts-ignore
						textureURL: './atlas/loading-s100-0.png',
						atlasURL: './atlas/loading-s100.json',
					},
					// using compressed multi atlas
					// NOTE: can't getPixel of compressed texture
					// NOTE: can't tileSprite of compressed texture
					// {
					//     type: "texture",
					//     key: LoaderFileKeys.loading,
					//     //@ts-ignore
					//     url: {
					//         "ASTC": { type: "PVR", multiAtlasURL: "./atlas/loading-astc.json", multiPath: "./atlas" },
					//         "S3TC": { type: "PVR", multiAtlasURL: "./atlas/loading-dxt5.json", multiPath: "./atlas" },
					//         "IMG": { multiAtlasURL: "./atlas/loading-png.json", multiPath: "./atlas" }
					//     }
					// },
					{
						type: 'json',
						key: LoaderFileKeys.sfx_json,
						url: './settings/sfx.json',
					},
				],
			},
		});
		// store globally accessible instance
		GameScene.instance = this;
	}

	public init(): void {
		// console.log("GameScene::init");

		if (this.CATCH_EXCEPTIONS_RELOAD_GAME) {
			// catch all to reload game
			window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
				alert('An unexpected error has occurred and the game will reload. Please try to "Continue" your game after the reload. If the error re-occurs, try starting a "New Game".');
				AppLocalStorage.SetLastVisitedScreenId(ScreenId.LOBBY);
				location.reload();
				return false;
			}
		}

		this.input.mouse.disableContextMenu();
		InitExtensionMethods();

		// sfx
		this.sfx_json = this.cache.json.get(LoaderFileKeys.sfx_json);

		LayoutDataManager.Init();
		// set and cache live data
		OptionsDataManager.Init(this);
		// Preload input defaults (for RESET). Also preload same Ids as cached from HDD.
		StatisticsDataManager.Init(this, [VariationId.BELOTE, VariationId.COINCHE, VariationId.CONTREE]);
		// LayoutDataManager.Init();
		CardDecksManager.Init(this);

		AppDebug.Init();

		// NOTE: uses AppDebug
		GTMTracking.Init();

		CardsManager.Init(this);
		// resolve localization (from debug, from cache, browser locale or just default)
		AppLocalization.Init();
		ScreenManager.Init(this);

		if (!AppDebug.SkipLoadingScreen()) {
			this.loadingScreen = new LoadingScreen(this);
			// this.loadingScreen.name = "loadingScreen";
			this.loadingScreen.Init(this.OnLoadingScreenHideStart.bind(this));
			this.loadingScreen.Build();
			// this.loadingScreen.Hide(false); // only shows once, so setup it that way
			this.loadingScreen.Show(false); // animate?
			this.loadingScreen.Start();
			this.loadingScreen.on(Phaser.Loader.Events.PROGRESS, this.OnLoadProgress, this);
			this.load.on(Phaser.Loader.Events.PROGRESS, this.OnLoadProgress, this);
			// NOTE: GameScene.create() runs before loadingScreen completes loading
			// bring loadingScreen on top of all other content
			this.loadingScreen.setDepth(1);
			this.add.existing(this.loadingScreen);
		}
		// build screen manager
		ScreenManager.Build();
	}

	public preload(): void {
		// console.log("GameScene::preload");

		// const path: string = './atlas';
		const scale: number = AppDebug.GetScalingVariantScale();

		const uiAtlasData = GetMultiAtlasScalingVariantDataByScale(UIVariantsData, scale);
		const lobbyAtlasData = GetMultiAtlasScalingVariantDataByScale(LobbyVariantsData, scale);
		const avatarsAtlasData = GetMultiAtlasScalingVariantDataByScale(AvatarsVariantsData, scale);
		const cardBackAtlasData = GetMultiAtlasScalingVariantDataByScale(
			ThemeDataManager.GetSharedData().options.itemsById[OptionsItemKeys.cardBack].source.variants,
			scale
		);
		const bgAtlasData = GetMultiAtlasScalingVariantDataByScale(
			ThemeDataManager.GetSharedData().options.itemsById[OptionsItemKeys.background].source.variants,
			scale
		);
		const themesAtlasData = GetMultiAtlasScalingVariantDataByScale(ThemesVariantsData, scale);

		CardDecksManager.Preload(scale);

		// texture
		this.load.multiatlas(LoaderFileKeys.ui, uiAtlasData.atlasURL, uiAtlasData.path);
		this.load.multiatlas(LoaderFileKeys.lobby, lobbyAtlasData.atlasURL, lobbyAtlasData.path);
		this.load.multiatlas(LoaderFileKeys.avatars, avatarsAtlasData.atlasURL, avatarsAtlasData.path);
		this.load.multiatlas(LoaderFileKeys.cardsBacks, cardBackAtlasData.atlasURL, cardBackAtlasData.path);
		this.load.multiatlas(LoaderFileKeys.bg, bgAtlasData.atlasURL, bgAtlasData.path);
		this.load.multiatlas(LoaderFileKeys.themes, themesAtlasData.atlasURL, themesAtlasData.path);

		// sfx
		this.load.audioSprite(LoaderFileKeys.sfx, this.sfx_json, this.sfx_json.resources);
		// console.log();
	}

	public create(): void {
		// return;
		// console.log("GameScene::create");
		// console.log(this.textures.get(LoaderFileKeys.bg))

		// track 'game loaded'
		GTMTracking.PLDTrackingGameLoad();

		if (AppDebug.IsDebug()) {
			logger.Init(this);
			logger.Build();
		}

		// AppDebug.log([
		//         `%cTexture Compression:`, "color:orange;font-weight:bold;",
		//         `${this.textures.get(LoaderFileKeys.lobby).source[0].compressionAlgorithm}`,
		//     ],
		//     AppDebugLogType.DEBUG,
		// );

		const languageId: LanguageId = AppLocalization.GetCurLanguageId();
		let background: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.background);
		const backgroundIndex: number = background && background != undefined ? (background.value as number) : 0;
		const bgItemData: OptionsItemThemeItemData = ThemeDataManager.GetOptionsItemThemeDataByItemId(OptionsItemKeys.background).items[backgroundIndex];

		// console.log("make sure aiLevel is selected based on cur selected table here?");
		// get aiLevel from (1) tableData or (if not exists) OptionsDataManager for selected variation.
		const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel).value as AILevel;

		this.bg = new SmartBackground(this);
		// this.bg.name = "LobbyScreen_bg";
		this.bg.Init(
			{ texture: LoaderFileKeys.bg, frame: bgItemData.source.textureFrame }, // bg
			null, // logo
			{ texture: LoaderFileKeys.ui, frame: 'bg-borders-01.png' }, // borders
			4 // borderScale
		);
		this.bg.Build();
		// this.bg.visible = false;
		// set bg data like bg animation
		this.bgData = bgItemData.data;
		this.bg.SetProps({
			bg: {
				...this.bg.GetDefaultProps().bg,
				...bgItemData.data,
			},
		});
		this.add.existing(this.bg);

		let lastPlayedVariationId: VariationId = AppLocalStorage.GetLastPlayedVariationId(null);
		// if we did not play variation before (fresh start)
		if (lastPlayedVariationId == null) {
				// get index 0 and translate it into variationId
				lastPlayedVariationId = LayoutDataManager.GetVariationIdByLobbyVariationItemIndex(LayoutDataManager.GetDefaultItemIndex());
		}
		console.log("lastPlayedVariationId="+lastPlayedVariationId);
		
		AppLocalization.SetCurVariationId(lastPlayedVariationId);
		StatisticsDataManager.SetVariationId(lastPlayedVariationId);
		// StatisticsDataManager.SetAILevel(aiLevel); // EDIT: set before table load (based on whether tableDAta exists or not)

		// card deck textures are now fully loaded.
		// build dynamic texture for 'current' card deck, shared among all views
		CardDecksManager.Build();

		// start listening resize change
		CardsManager.Build();
		AppObjectPool.Init(this);
		AppAudioPlayer.Init(this);
		AppAudioPlayer.Build();

		// AppAudioPlayer.PlayMusicTrackByIndex(AppLocalStorage.GetMusicTrackIndex());
		// const curMusicTrackIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.musicTrack).value as number;
		// AppAudioPlayer.PlayMusicTrackByIndex(curMusicTrackIndex);
		FocusManager.Init(this, false);

		// lobbyScreen
		this.lobbyScreen = new LobbyScreen(this);
		this.lobbyScreen.Init(
			this.OnLobbyBurgerClick.bind(this),
			this.OnLobbyNewGameClick.bind(this),
			this.OnLobbyContinueClick.bind(this),
			this.OnLobbyVariationHelpClick.bind(this),
			this.OnLobbyVariationStatsClick.bind(this)
		);
		this.lobbyScreen.Build();
		this.lobbyScreen.Hide(false);
		this.add.existing(this.lobbyScreen);
		// optionsScreen
		this.optionsScreen = new OptionsScreen(this);
		this.optionsScreen.Init(
			this.OnOptionsCloseClick.bind(this)
			// this.OnOptionsBackClick.bind(this)
		);
		this.optionsScreen.Build();
		this.optionsScreen.Hide(false);
		// this.optionsScreen.Show();
		this.add.existing(this.optionsScreen);

		this.tableScreen = new TableScreen(this);
		this.tableScreen.name = 'tableScreen';
		this.tableScreen.Init(this.OnTableBackClick.bind(this));
		this.tableScreen.Build();
		this.tableScreen.SetVariationId(lastPlayedVariationId);
		this.tableScreen.SetLanguageId(languageId);
		this.tableScreen.GetTableAI().SetAILevel(aiLevel);
		this.tableScreen.Hide(false);
		this.add.existing(this.tableScreen);

		this.dealCompleteScreen = new DealCompleteScreen(this);
		// this.dealCompleteScreen.Init(lastPlayedVariationId, languageId, this.OnDealCompleteLobbyClick.bind(this));
		this.dealCompleteScreen.Init(lastPlayedVariationId, languageId);
		this.dealCompleteScreen.Build();
		this.dealCompleteScreen.Hide(false);
		this.add.existing(this.dealCompleteScreen);

		// statisticsScreen
		this.statisticsScreen = new StatisticsScreen(this);
		this.statisticsScreen.Init(
			this.OnStatisticsCloseClick.bind(this),
			lastPlayedVariationId,
			// LanguageId.ENGLISH,
			AppLocalization.GetCurLanguageId()
		);
		this.statisticsScreen.Build();
		this.statisticsScreen.Hide(false);
		// this.statisticsScreen.Show();
		this.add.existing(this.statisticsScreen);

		// rulesScreen
		this.rulesScreen = new RulesScreen(this);
		this.rulesScreen.Init(this.OnRulesCloseClick.bind(this));
		this.rulesScreen.Build();
		// this.rulesScreen.SetData(
		//     lastPlayedVariationId,
		//     AppLocalization.GetCurLanguageId(),
		// );
		this.rulesScreen.Hide(false);
		// this.rulesScreen.Show();
		this.add.existing(this.rulesScreen);

		// Start
		// if not debugging using 'skipLoading'
		if (this.loadingScreen != undefined) {
			if (this.loadingScreen.IsLoadComplete()) {
				this.OnGameLoadComplete();
			} else {
				this.loadingScreen.once(LoadingScreenEvents.LOAD_COMPLETE, () => {
					this.OnGameLoadComplete();
				});
			}
		}
		// loading screen is commented out (debugging something else)
		// just show lobby
		else {
			// DEBUG: just show
			// this.lobbyScreen.Show();
			this.OnGameLoadComplete();
		}

		this.events.on(Phaser.Scenes.Events.UPDATE, this.Update, this);
		ScreenManager.GetEventEmitter().on(ScreenManagerEvents.RESIZE, this.OnResize, this);
		OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.BACKGROUND_CHANGE, this.OnBackgroundDataChange, this);

		// called again to refresh other components (after loading finished)
		ScreenManager.OnResizeManual();
	}

	private OnLoadProgress(perc: number): void {
		// console.log("OnLoadProgress; perc="+perc);

		this.loadingScreen.SetLoadedPerc(perc);
	}

	private OnGameLoadComplete(): void {
		const curMusicTrackIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.musicTrack).value as number;
		AppAudioPlayer.PlayMusicTrackByIndex(curMusicTrackIndex);
		const debugTableData: TableData = AppDebug.GetTableData();
		const lastVariationId: VariationId = this.lobbyScreen.GetCurVariationId();
		const lastVisitedScreenId: ScreenId = AppLocalStorage.GetLastVisitedScreenId(null);
		const tableData: TableData = AppLocalStorage.GetTableData(lastVariationId);
		const hasLastVariationTableData: boolean = tableData != null;

		// if got DEBUG table data
		if (debugTableData != null) {
			this.StartTableFromDebug(debugTableData);
		}
		else {
			// if have already played a variation before, AND
			// if last visited screen (user left from) was table screen, AND
			// if we got cached tableData for that variation
			if (lastVariationId != null && lastVisitedScreenId == ScreenId.TABLE && hasLastVariationTableData) {
				// EDIT: take aiLevel from tableData
				// const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel).value as AILevel;
				const aiLevel: AILevel = tableData.options.aiLevel as AILevel;
				AppLocalStorage.SetLastPlayedVariationId(lastVariationId);
				AppLocalization.SetCurVariationId(lastVariationId);
				StatisticsDataManager.SetVariationId(lastVariationId);
				// StatisticsDataManager.SetAILevel(aiLevel);
				this.lobbyScreen.SetCurVariationId(lastVariationId);
				this.lobbyScreen.SetLayout(LobbyLayout.SINGLE, true);
				this.tableScreen.GetTableAI().SetAILevel(aiLevel);
				this.tableScreen.Start(false);
				const animate: boolean = !this.tableScreen.GetStateMachine().GetDynamicDataDealCompleteShowing();
				this.tableScreen.Show(animate);

				// HOTFIX: if dealcompletescreen should be visible, make table invis (avoids bit of a glitch)
				if (this.tableScreen.GetStateMachine().GetDynamicDataDealCompleteShowing()) {
					this.tableScreen.visible = false;
				}
			}
			// otherwise
			else {
				// show bg
				this.bg.Show();
				// show lobby
				this.lobbyScreen.Show(true);
				// flag last visited screen
				AppLocalStorage.SetLastVisitedScreenId(ScreenId.LOBBY);
			}
		}
	}

	/**
	 * Loading screen started to Hide
	 */
	private OnLoadingScreenHideStart(): void {
		// console.log(this)
		// show Lobby
		// this.lobbyScreen.Show();
	}

	private StartTableFromDebug(debugTableData: TableData): void {
		// EDIT: aiLevel now from tableData
		// const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel).value as AILevel;
		const aiLevel: AILevel = debugTableData.options.aiLevel as AILevel;
		// AppLocalStorage.SetLastPlayedVariationId(debugTableData.variationId);
		AppLocalization.SetCurVariationId(debugTableData.variationId);
		this.tableScreen.SetVariationId(debugTableData.variationId);
		this.tableScreen.SetLanguageId(AppLocalization.GetCurLanguageId());
		this.tableScreen.GetTableAI().SetAILevel(aiLevel);
		StatisticsDataManager.SetVariationId(debugTableData.variationId);
		// StatisticsDataManager.SetAILevel(aiLevel);
		const stateMachine: TableStateMachine = this.tableScreen.GetStateMachine();

		let tableData: TableData = stateMachine.GetDataNewGame(debugTableData.variationId);

		// reset table (pool cards, etc)
		// this.tableScreen.Reset();

		// override
		tableData = {
			// Use 'new game data' as templacte to override with our custom data.
			...tableData,
			// ditch initStackCards from 'new game data model' (default, can override via debug below)
			...{ initStackCards: [] },
			// 4 is same in each variation: 'RESOLVE_PLAY_CARD' (default, can override via debug below)
			...{ state: { taskType: 4, taskSubType: 5 } },
			// further index fix for state 4, south as attacker
			...{
				dealDealerIndex: 2,
				trickDealerIndex: 1,
				lastPlayedPlayerIndex: 1,
				playerAttackerIndex: 2,
			},
			// default trump suit
			...{ trumpSuit: TrumpSuit.HEARTS },
			// override with what ever you need
			...debugTableData,
		};

		stateMachine.SetData(tableData);
		stateMachine.UpdateViewFromData();
		stateMachine.Start(true, false);

		this.tableScreen.GetStateMachine().SetRestoredFromCache(true);

		this.tableScreen.Show();
	}

	private OnLobbyNewGameClick(): void {
		// console.log("OnLobbyNewGameClick");

		const curFocusedVariationId: VariationId = this.lobbyScreen.GetCurVariationId();
		// console.log("OnLobbyNewGameClick", VariationId[curFocusedVariationId]);
		this.tableScreen.SetVariationId(curFocusedVariationId);
		const stateMachine: TableStateMachine = this.tableScreen.GetStateMachine();

		AppLocalStorage.SetLastPlayedVariationId(curFocusedVariationId);
		AppLocalization.SetCurVariationId(curFocusedVariationId);
		StatisticsDataManager.SetVariationId(curFocusedVariationId);

		stateMachine.GetSetDataNewGame(curFocusedVariationId);
		const tableData: TableData = stateMachine.GetData();
		// EDIT: aiLevel from tableData
		// const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel).value as AILevel;
		const aiLevel: AILevel = tableData.options.aiLevel as AILevel;

		// track 'new game'
		GTMTracking.PLDTrackingLevelStart(VariationId[curFocusedVariationId].toString(), GTMTrackingContinueOrNewGame.NEW);
		this.tableScreen.SetLanguageId(AppLocalization.GetCurLanguageId());
		this.tableScreen.GetTableAI().SetAILevel(aiLevel);
		// StatisticsDataManager.SetAILevel(aiLevel);
		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);

		// hide table content
		this.tableScreen.HideContent();

		// set stateMachine data
		// reset table (pool cards, etc)
		// this.tableScreen.Reset();
		//
		// build table view from said data
		stateMachine.UpdateViewFromData();
		// > TaskStart
		// uses data already set (above)
		stateMachine.Start(false, true);
		// fade in table
		// > OnShowStart: fade in table contents from state
		// > ManualResize
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
		this.lobbyScreen.Hide();
		this.tableScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
		this.tableScreen.Show();
		// flag last visited screen
		AppLocalStorage.SetLastVisitedScreenId(ScreenId.TABLE);
	}

	private OnLobbyContinueClick(): void {
		const curFocusedVariationId: VariationId = this.lobbyScreen.GetCurVariationId();
		const tableData: TableData = AppLocalStorage.GetTableData(curFocusedVariationId);
		// EDIT: aiLevel now from tableData
		// const aiLevel: AILevel = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.aiLevel).value as AILevel;
		const aiLevel: AILevel = tableData.options.aiLevel as AILevel;

		// track 'continue game'
		GTMTracking.PLDTrackingLevelStart(VariationId[curFocusedVariationId].toString(), GTMTrackingContinueOrNewGame.CONTINUE);
		AppLocalStorage.SetLastPlayedVariationId(curFocusedVariationId);
		// untested: some table components might take it from here
		AppLocalization.SetCurVariationId(curFocusedVariationId);
		// set cur focused variation id to table
		this.tableScreen.SetVariationId(curFocusedVariationId);
		this.tableScreen.SetLanguageId(AppLocalization.GetCurLanguageId());
		this.tableScreen.GetTableAI().SetAILevel(aiLevel);
		StatisticsDataManager.SetVariationId(curFocusedVariationId);
		// StatisticsDataManager.SetAILevel(aiLevel);

		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);

		// // hide all table components/screens/etc (except bg)
		// this.tableScreen.HideContent(false); // animate? onComplete?

		// hide table content
		this.tableScreen.HideContent();

		// // start next table Deal
		// // > sets data (based on availability from cache)
		// // > stateMachine.UpdateViewFromData
		// // > stateMachine.Start
		this.tableScreen.Start(true);

		// fade in table
		// > OnShowStart: fade in table contents from state
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
		this.lobbyScreen.Hide();
		this.tableScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
		this.tableScreen.Show();

		// HOTFIX: if dealcompletescreen should be visible, make table invis (avoids bit of a glitch)
		if (this.tableScreen.GetStateMachine().GetDynamicDataDealCompleteShowing()) {
			this.tableScreen.visible = false;
		}

		// flag last visited screen
		AppLocalStorage.SetLastVisitedScreenId(ScreenId.TABLE);
	}

	private OnLobbyBurgerClick(): void {
		const curVariationId: number = this.lobbyScreen.GetCurVariationId();
		// console.log(VariationId[curVariationId]);
		this.optionsScreen.SetData(curVariationId, AppLocalization.GetCurLanguageId());

		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
		this.lobbyScreen.Hide();
		this.optionsScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
		this.optionsScreen.Show();

		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
	}

	/**
	 * Options Close button was clicked by user
	 * - check for change in settings and if changed, re-cacghed and save to HDD
	 */
	private OnOptionsCloseClick(): void {
		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);

		this.optionsScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
		this.optionsScreen.Hide();
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
		this.lobbyScreen.Show();
	}

	private OnLobbyVariationHelpClick(item: LobbyVariationItem): void {
		// console.log("OnLobbyVariationHelpClick");

		this.rulesScreen.SetData(
			item.variationId,
			// this.rulesScreen.curVariationId == VariationId.BELOTE ? VariationId.COINCHE : VariationId.BELOTE,
			AppLocalization.GetCurLanguageId()
			// this.rulesScreen.curlangu > 0.5 ? AppLocalization.GetCurLanguageId()
		);

		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
		this.lobbyScreen.Hide();
		this.rulesScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
		this.rulesScreen.Show();
	}

	private OnRulesCloseClick(): void {
		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);

		this.rulesScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
		this.rulesScreen.Hide();
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
		this.lobbyScreen.Show();
	}

	private OnLobbyVariationStatsClick(item: LobbyVariationItem): void {
		// console.log("OnLobbyVariationStatsClick");

		this.statisticsScreen.SetData(
			item.variationId,
			// this.rulesScreen.curVariationId == VariationId.BELOTE ? VariationId.COINCHE : VariationId.BELOTE,
			AppLocalization.GetCurLanguageId()
			// this.rulesScreen.curlangu > 0.5 ? AppLocalization.GetCurLanguageId()
		);

		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
		this.lobbyScreen.Hide();
		this.statisticsScreen.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
		this.statisticsScreen.Show();
	}

	private OnStatisticsCloseClick(): void {
		// button click
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);

		this.statisticsScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
		this.statisticsScreen.Hide();
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
		this.lobbyScreen.Show();
	}

	/**
	 * Clear all timers
	 * Kill all tweens
	 */
	public KillTimers(): void {
		this.tweens.killAll();
		this.time.removeAllEvents();
		this.time.clearPendingEvents();
	}

	private OnTableBackClick(): void {
		this.KillTimers();
		// reset table (pool cards, etc)
		this.tableScreen.Reset();
		AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
		this.tableScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
		this.tableScreen.Hide();
		this.lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
		this.lobbyScreen.Show();
		AppLocalStorage.SetLastVisitedScreenId(ScreenId.LOBBY);
	}

	private OnBackgroundDataChange(itemData: ChangedItemData): void {
		this.bg.SetTile({
			texture: LoaderFileKeys.bg,
			frame: itemData.theme.items[itemData.live.value as number].source.textureFrame,
		});
		this.bgData = itemData.theme.items[itemData.live.value as number].data;
		this.bg.SetProps({
			bg: {
				...this.bg.GetDefaultProps().bg,
				...this.bgData,
			},
		});
		// console.log(this.bgData);
	}

	private OnResize(windowRect: RectSize): void {
		// console.log("GameScene::OnResize");

		// const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
		// const assetScale: number = ScreenManager.GetAssetScale(); // baseScale?
		// const screenCenterX: number = (0.5 * windowRect.width)|0;

		// bg
		this.bg.OnResize(windowRect);
	}

	private Update(time: number, delta: number): void {
		if (this.bgData?.update) {
			this.bg.Update(time, delta);
		}
	}
}
